import { ref } from 'vue';
import { useGetGroup, useGroupMembers, CustomerMgmtTypes, useGetUsers } from '@ilteducation/customer-mgmt';
import { captureMessage } from '@sentry/vue';

export type Member = CustomerMgmtTypes.MembershipOutput & {
  user: CustomerMgmtTypes.UserOutput;
  associations: CustomerMgmtTypes.AssociationOutput[];
};

const group = ref<CustomerMgmtTypes.GetGroupResponse | null>(null);
const members = ref<Member[] | null>(null);
const isMembersLoading = ref<boolean>(false);
const isGroupLoading = ref<boolean>(false);
export const useSidebarGroup = () => {
  const { loadGroup } = useGetGroup();
  const { fetchAllUsers } = useGetUsers();
  const { loadAllMembers } = useGroupMembers();

  const loadGroupProfile = async (uid: string) => {
    if (isGroupLoading.value) {
      return;
    }

    isGroupLoading.value = true;
    const fetchedGroup = await loadGroup(uid);
    group.value = fetchedGroup;
    isGroupLoading.value = false;
  };

  const getMembers = async (groupUid: string, organizationUid: string): Promise<Member[] | null> => {
    if (isMembersLoading.value) {
      return members.value;
    }

    isMembersLoading.value = true;
    members.value = null;
    const [fetchedGroupMembers, users] = await Promise.all([
      loadAllMembers(groupUid),
      fetchAllUsers({ filters: { schoolUnits: [organizationUid], groups: [groupUid] } }),
    ]);

    const userByUid: Record<string, CustomerMgmtTypes.UserOutput> = {};
    users.forEach((user) => {
      userByUid[user.uid] = user;
    });
    const membersWithAssociationAndFullUser = (fetchedGroupMembers ?? [])
      .map((member): Omit<Member, 'associations'> & { associations?: CustomerMgmtTypes.AssociationOutput[] } => {
        const associations =
          userByUid[member.user.uid]?.associations?.map((assoc) => ({
            ...assoc,
            user: member.user,
          })) ?? undefined;

        if (!Array.isArray(associations)) {
          captureMessage('user fetched by group uid could not be found by membership.user uid.', (scope) => {
            scope.setExtra('request-info', { memberUserUid: member.user.uid, groupUid });
            scope.setLevel('warning');
            return scope;
          });
        }

        return {
          ...member,
          associations,
          user: userByUid[member.user.uid],
        };
      })
      .filter((member): member is Member => {
        if (Array.isArray(member.associations)) {
          return true;
        }

        return false;
      });

    members.value = membersWithAssociationAndFullUser;

    isMembersLoading.value = false;

    return members.value;
  };

  const getGroup = async (groupUid: string, organizationUid: string, reFetch: boolean = false) => {
    if (reFetch || !group.value || group.value.uid !== groupUid) {
      await Promise.all([loadGroupProfile(groupUid), getMembers(groupUid, organizationUid)]);
    }
  };

  return {
    group,
    members,
    isMembersLoading,
    isGroupLoading,
    getGroup,
    getMembers,
  };
};
